import { ReactElement, useEffect, useLayoutEffect, useRef } from "react";
import classNames from "classnames";
import {
  UiaButtonIcon,
  UiaButtonLabelIcon,
  UiaTextBody,
  UiaTextSubheading,
  useUiContext,
} from "@rotorstudios/fe-ui";
import QRCode from "react-qr-code";
import { gsap } from "@rotorstudios/fe-modules";

import useUiMQRCodeOverlay from "./useUiMQRCodeOverlay";
import UiaIconArrowLink from "../../uiComponents/atoms/uiaIconArrowLink/UiaIconArrowLink";
import UiaIconClose from "../../uiComponents/atoms/uiaIconClose/UiaIconClose";
import { isDesktopUserAgent } from "../../helpers/utils";
import "./_UiMQRCodeOverlay.scss";
import { createPortal } from "react-dom";

type UiMQRCodeOverlayProps = {
  className?: string;
  tTitle: string;
  tBody: string;
  tLabel: string;
  open: boolean;
  setOpen: (open: boolean) => void;
};

const UiMQRCodeOverlay = ({
  className = "",
  tTitle,
  tBody,
  tLabel,
  open,
  setOpen,
}: UiMQRCodeOverlayProps): ReactElement => {
  const { projectClass, breakpointActive, isDesktop } = useUiContext();
  const { arUrl } = useUiMQRCodeOverlay();

  const qrCodeOverlayRef = useRef<any>(null);
  const baseRef = useRef<any>(null);

  const popUpTimeline = useRef<GSAPTimeline>(
    gsap.timeline({
      defaults: {
        ease: "expo.out",
      },
    }),
  );

  const portalRoot = document.getElementById("EBRO_overlay-root")!;

  useEffect(() => {
    if (open) {
      popUpTimeline.current.play();
    }
  }, [open]);

  useEffect(() => {
    const backdrop = baseRef.current;
    if (!backdrop) return;

    const handleClick = (e: MouseEvent) => {
      if (e.target === backdrop) {
        popUpTimeline.current.reverse();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        popUpTimeline.current.reverse();
      }
    };

    backdrop.addEventListener("click", handleClick);
    globalThis?.window.addEventListener("keydown", handleKeyDown);

    return () => {
      backdrop.removeEventListener("click", handleClick);
      globalThis?.window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      if (isDesktopUserAgent()) {
        const tl = popUpTimeline.current;
        tl.add("start")
          .to(
            baseRef.current,
            {
              duration: 0.5,
              display: "flex",
              onReverseComplete: () => {
                setOpen(false);
              },
            },
            "start",
          )
          .from(
            qrCodeOverlayRef.current,
            {
              duration: 0.5,
              scale: 0,
              opacity: 0,
            },
            "start",
          )
          .reverse();
      } else {
        popUpTimeline.current?.kill();
        gsap.to(qrCodeOverlayRef.current, {
          clearProps: "scale,opacity",
        });
      }
    }, baseRef);
    return () => {
      ctx?.revert();
    };
  }, [isDesktop, setOpen]);

  const getClassName = () =>
    classNames(`${projectClass}_UiMQRCodeOverlay`, className, breakpointActive);

  return createPortal(
    <div ref={baseRef} className={getClassName()}>
      <dialog
        ref={qrCodeOverlayRef}
        className={`${projectClass}_UiMQRCodeOverlay_Dialog`}
      >
        <UiaTextSubheading tText={tTitle} LARGE />
        <UiaTextBody tText={tBody} />
        <QRCode
          className={`${projectClass}_UiMQRCodeOverlay_QRCode`}
          size={170}
          bgColor="white"
          fgColor="black"
          value={arUrl}
        />
        <UiaButtonLabelIcon
          inverse
          pill
          icon={<UiaIconArrowLink right />}
          tLabel={tLabel}
          onClick={() => {
            popUpTimeline.current.reverse();
          }}
        />
        <UiaButtonIcon
          icon={<UiaIconClose />}
          onClick={() => {
            popUpTimeline.current.reverse();
          }}
          circular
        />
      </dialog>
    </div>,
    portalRoot,
  );
};

export default UiMQRCodeOverlay;
